import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  Plus,
  Route,
  CarFront,
  Users,
  MessageSquare,
  DollarSign,
  User,
  Star,
  Bell,
  Compass,
  Menu,
  X,
  LogOut,
  Settings,
  ChevronDown,
  Search,
  HelpCircle,
  Building2,
  CreditCard,
  Map,
  ClipboardList,
  Store,
  CalendarCheck,
} from 'lucide-react';
import { useAuth } from '../../../features/auth/contexts/useAuth';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isOperator = user?.type === 'operator';
  const hasFleet = user?.operator_type === 'transporter' || user?.operator_type === 'tour_operator';
  const isTourOperator = user?.operator_type === 'tour_operator';

  // Build operator sidebar sections to match Laravel company sidebar structure
  const operatorSections: NavSection[] = [
    {
      title: 'Main Menu',
      items: [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      ],
    },
    {
      title: 'Fleet Management',
      items: [
        { label: 'Vehicles', path: '/fleet', icon: CarFront },
        { label: 'Safari Trips', path: '/safaris-management', icon: Map },
      ],
    },
    {
      title: 'Business',
      items: [
        { label: 'Bookings', path: '/bookings', icon: ClipboardList },
        { label: 'Marketplace', path: '/marketplace', icon: Store },
        { label: 'Hire Requests', path: '/hire-requests', icon: CalendarCheck },
        { label: 'Earnings', path: '/dashboard/earnings', icon: DollarSign },
        { label: 'Reviews', path: '/reviews', icon: Star },
      ],
    },
    {
      title: 'Settings',
      items: [
        { label: 'Settings', path: '/settings', icon: Settings },
      ],
    },
  ];

  // Traveler sidebar sections
  const travelerSections: NavSection[] = [
    {
      items: [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Explore', path: '/marketplace', icon: Compass },
        { label: 'My Bookings', path: '/bookings', icon: Calendar },
        { label: 'Saved', path: '/saved', icon: Star },
      ],
    },
    {
      title: 'Discover',
      items: [
        { label: 'Reviews', path: '/reviews', icon: Star },
      ],
    },
    {
      title: 'Account',
      items: [
        { label: 'Payments', path: '/payments', icon: CreditCard },
        { label: 'Profile', path: '/profile', icon: User },
      ],
    },
  ];

  const sidebarSections = isOperator ? operatorSections : travelerSections;

  // For the mobile bottom nav, pick the first few key items
  const mobileNavItems: NavItem[] = isOperator
    ? [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Vehicles', path: '/fleet', icon: CarFront },
        { label: 'Bookings', path: '/bookings', icon: ClipboardList },
        { label: 'Earnings', path: '/dashboard/earnings', icon: DollarSign },
      ]
    : [
        { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { label: 'Explore', path: '/marketplace', icon: Compass },
        { label: 'Bookings', path: '/bookings', icon: Calendar },
        { label: 'Saved', path: '/saved', icon: Star },
      ];

  const isActivePath = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const NavLink = ({ item }: { item: NavItem }) => {
    const Icon = item.icon;
    const isActive = isActivePath(item.path);
    
    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[14px] font-medium transition-colors ${
          isActive
            ? 'bg-neutral-900 text-white'
            : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100'
        }`}
      >
        <Icon className={`w-[18px] h-[18px] ${isActive ? 'text-white' : 'text-neutral-400'}`} />
        <span>{item.label}</span>
        {item.badge && (
          <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full font-medium ${
            isActive ? 'bg-white/20 text-white' : 'bg-neutral-200 text-neutral-600'
          }`}>
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-neutral-200 z-40 flex items-center px-4">
        <div className="flex items-center gap-3 lg:w-60">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-1.5 rounded-md hover:bg-neutral-100"
          >
            <Menu className="w-5 h-5 text-neutral-600" />
          </button>
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-neutral-900 rounded-lg flex items-center justify-center">
              <Route className="w-4 h-4 text-white" />
            </div>
            <span className="text-[15px] font-semibold text-neutral-900 hidden sm:block">DalaRide</span>
          </Link>
        </div>

        {/* Search Bar */}
        <div className="flex-1 max-w-xl mx-4 hidden md:block">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search trips, passengers, bookings..."
              className="w-full h-9 pl-10 pr-4 bg-neutral-100 border-0 rounded-lg text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-neutral-400 bg-white rounded border border-neutral-200">
              ⌘K
            </kbd>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-2 ml-auto">
          {isOperator && (
            <button
              onClick={() => navigate('/dashboard/trips/new')}
              className="hidden sm:flex items-center gap-1.5 h-8 px-3 bg-neutral-900 text-white text-sm font-medium rounded-lg hover:bg-neutral-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>New Trip</span>
            </button>
          )}
          
          <button className="p-2 rounded-lg hover:bg-neutral-100 relative">
            <Bell className="w-5 h-5 text-neutral-600" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>

          <button className="p-2 rounded-lg hover:bg-neutral-100 hidden sm:block">
            <HelpCircle className="w-5 h-5 text-neutral-600" />
          </button>

          {/* User Menu */}
          <div className="relative">
            <button 
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-neutral-100"
            >
              <div className="w-7 h-7 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-medium text-neutral-600">
                {user?.first_name?.charAt(0) || 'U'}
              </div>
              <ChevronDown className="w-4 h-4 text-neutral-400 hidden sm:block" />
            </button>
            
            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-20">
                  <div className="px-3 py-2 border-b border-neutral-100">
                    <p className="text-sm font-medium text-neutral-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-neutral-500">{user?.email}</p>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                  <Link
                    to="/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-50"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                  <div className="border-t border-neutral-100 mt-1 pt-1">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" />
                      Log out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-14 left-0 bottom-0 w-60 bg-white border-r border-neutral-200 z-40 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Close */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-neutral-100">
            <span className="text-sm font-medium text-neutral-900">Menu</span>
            <button onClick={() => setSidebarOpen(false)} className="p-1 rounded hover:bg-neutral-100">
              <X className="w-5 h-5 text-neutral-500" />
            </button>
          </div>

          {/* Main Navigation */}
          <nav className="flex-1 overflow-y-auto p-3 space-y-1">
            {sidebarSections.map((section, sIdx) => (
              <div key={sIdx} className={sIdx > 0 ? 'pt-4 mt-4 border-t border-neutral-100' : ''}>
                {section.title && (
                  <p className="px-3 mb-2 text-[11px] font-medium text-neutral-400 uppercase tracking-wide">
                    {section.title}
                  </p>
                )}
                {section.items.map(item => (
                  <NavLink key={item.path + item.label} item={item} />
                ))}
              </div>
            ))}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-neutral-100">
            <div className="flex items-center gap-3 px-3 py-2 bg-neutral-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center text-sm font-medium text-neutral-600">
                {user?.first_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-neutral-500">
                  {isOperator ? (hasFleet ? 'Transporter' : 'Operator') : 'Traveler'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-60 pt-14 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-14 bg-white border-t border-neutral-200 z-30 flex items-center justify-around px-2">
        {mobileNavItems.map(item => {
          const Icon = item.icon;
          const isActive = isActivePath(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center w-16 h-full ${
                isActive ? 'text-neutral-900' : 'text-neutral-400'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] mt-0.5 font-medium">{item.label.split(' ')[0]}</span>
            </Link>
          );
        })}
        <Link
          to="/profile"
          className={`flex flex-col items-center justify-center w-16 h-full ${
            isActivePath('/profile') ? 'text-neutral-900' : 'text-neutral-400'
          }`}
        >
          <User className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 font-medium">Profile</span>
        </Link>
      </nav>
    </div>
  );
};
